import { Children, isValidElement, ReactElement, ReactNode } from "react";

/**
 * Gets a single child element with the specified slot name
 *
 * @param children The children to search through
 * @param slot The slot name to look for
 * @returns The child element with the specified slot, or undefined if none is found
 */
export const getSlot = (children: ReactNode, slot: string): ReactElement | undefined => {
  return Children.toArray(children).find(
    (child): child is ReactElement => isValidElement(child) && child.props.slot === slot
  );
};

/**
 * Gets all child elements with the specified slot name
 *
 * @param children The children to search through
 * @param slot The slot name to look for
 * @returns An array of child elements with the specified slot
 */
export const getSlots = (children: ReactNode, slot: string): ReactElement[] => {
  const results: ReactElement[] = [];

  const processChildren = (nodes: ReactNode) => {
    Children.forEach(nodes, child => {
      if (isValidElement(child)) {
        if (child.props.slot === slot) {
          results.push(child);
        }
        if (child.props.children) {
          processChildren(child.props.children);
        }
      }
    });
  };

  processChildren(children);
  return results;
};

/**
 * Gets all child elements that don't have a slot name specified
 *
 * @param children The children to search through
 * @param namedSlots Array of slot names to exclude
 * @returns An array of child elements without the specified slots
 */
export const getUnslottedChildren = (children: ReactNode): (ReactElement | string)[] => {
  const results: (ReactElement | string)[] = [];

  const processChildren = (nodes: ReactNode) => {
    Children.forEach(nodes, child => {
      if (typeof child === "string") {
        results.push(child);
        return;
      }

      if (!isValidElement(child)) {
        return;
      }

      if (child.props.slot) {
        return;
      }

      results.push(child);
    });
  };

  processChildren(children);
  return results;
};
